import { useEffect, useMemo, useState } from "react";
import { Check, Copy, Link2, Trash2 } from "lucide-react";
import { CANONICAL_HOST } from "../../config";
import CampaignNotes from "./CampaignNotes";

const API = process.env.REACT_APP_BACKEND_URL;

const PAGES = [
  { value: "/", label: "Home" },
  { value: "/pricing", label: "Pricing" },
  { value: "/features", label: "Features" },
  { value: "/product-tour", label: "Product tour" },
  { value: "/how-it-works", label: "How it works" },
  { value: "/contact", label: "Contact" },
];

const SOURCES = [
  { value: "facebook", label: "Facebook", medium: "paid_social" },
  { value: "instagram", label: "Instagram", medium: "paid_social" },
  { value: "zeely", label: "Zeely", medium: "paid_social" },
  { value: "google", label: "Google", medium: "cpc" },
  { value: "tiktok", label: "TikTok", medium: "paid_social" },
  { value: "linkedin", label: "LinkedIn", medium: "paid_social" },
  { value: "email", label: "Email", medium: "email" },
  { value: "custom", label: "Custom…", medium: "referral" },
];

export const slug = (v) =>
  v.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);

const field = "w-full px-3.5 py-2.5 rounded-xl border border-[#D9DEE4] bg-white text-[14px] focus:outline-none focus:ring-2 focus:ring-[#334E68]";
const lbl = "block text-[12px] font-semibold uppercase tracking-[0.08em] text-[#5B6B7B] mb-1.5";

function CopyButton({ text, testid }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (_) {
      const t = document.createElement("textarea");
      t.value = text;
      document.body.appendChild(t);
      t.select();
      document.execCommand("copy");
      t.remove();
    }
    setDone(true);
    setTimeout(() => setDone(false), 1500);
  };
  return (
    <button type="button" onClick={copy} data-testid={testid} aria-label="Copy link"
      className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#334E68] hover:bg-[#22384F] text-white text-[13px] font-semibold transition-colors">
      {done ? <Check size={14} /> : <Copy size={14} />} {done ? "Copied" : "Copy"}
    </button>
  );
}

export default function LinkBuilder({ token }) {
  const [page, setPage] = useState("/");
  const [source, setSource] = useState("facebook");
  const [custom, setCustom] = useState("");
  const [campaign, setCampaign] = useState("");
  const [saved, setSaved] = useState([]);
  const [error, setError] = useState("");
  const auth = { Authorization: `Bearer ${token}` };

  const src = SOURCES.find((s) => s.value === source);
  const sourceValue = source === "custom" ? slug(custom) : source;
  const campaignValue = slug(campaign);
  const url = useMemo(() => {
    if (!sourceValue || !campaignValue) return "";
    const q = new URLSearchParams({ utm_source: sourceValue, utm_medium: src.medium, utm_campaign: campaignValue });
    return `${CANONICAL_HOST}${page}?${q.toString()}`;
  }, [page, sourceValue, campaignValue, src.medium]);

  useEffect(() => {
    fetch(`${API}/api/marketing/stats/links`, { headers: auth })
      .then((r) => (r.ok ? r.json() : { links: [] }))
      .then((d) => setSaved(d.links || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const save = async () => {
    setError("");
    try {
      const r = await fetch(`${API}/api/marketing/stats/links`, {
        method: "POST",
        headers: { ...auth, "Content-Type": "application/json" },
        body: JSON.stringify({ url, page, source: sourceValue, medium: src.medium, campaign: campaignValue }),
      });
      if (r.status === 401) return setError("Your session expired. Lock and unlock the page, then try again.");
      if (!r.ok) throw new Error();
      const doc = await r.json();
      setSaved((list) => [doc, ...list.filter((l) => l.id !== doc.id)]);
    } catch (_) {
      setError("Could not save this link. Please try again.");
    }
  };

  const remove = async (id) => {
    setError("");
    try {
      const r = await fetch(`${API}/api/marketing/stats/links/${id}`, { method: "DELETE", headers: auth });
      if (!r.ok) throw new Error();
      setSaved((list) => list.filter((l) => l.id !== id));
    } catch (_) {
      setError("Could not delete this link. Please try again.");
    }
  };

  return (
    <section className="bg-white rounded-2xl border border-[#E6E1D8] p-5 sm:p-6" data-testid="link-builder">
      <div className="flex items-center gap-2 mb-1">
        <Link2 size={18} className="text-[#E8712C]" />
        <h2 className="font-semibold text-[16px] text-[#1A2A3A]">Ad link builder</h2>
      </div>
      <p className="text-[13px] text-[#5B6B7B] mb-5">Use a tagged link in every ad so its visits and clicks show up under the right source and campaign.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className={lbl} htmlFor="lb-page">Page</label>
          <select id="lb-page" className={field} value={page} onChange={(e) => setPage(e.target.value)} data-testid="link-builder-page-select">
            {PAGES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
        <div>
          <label className={lbl} htmlFor="lb-source">Source</label>
          <select id="lb-source" className={field} value={source} onChange={(e) => setSource(e.target.value)} data-testid="link-builder-source-select">
            {SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          {source === "custom" && (
            <input className={`${field} mt-2`} placeholder="e.g. newsletter-partner" value={custom}
              onChange={(e) => setCustom(e.target.value)} data-testid="link-builder-custom-source-input" aria-label="Custom source" />
          )}
        </div>
        <div>
          <label className={lbl} htmlFor="lb-campaign">Campaign name</label>
          <input id="lb-campaign" className={field} placeholder="e.g. Launch week" value={campaign}
            onChange={(e) => setCampaign(e.target.value)} data-testid="link-builder-campaign-input" />
        </div>
      </div>
      <div className="mt-5 rounded-xl bg-[#FAF7F2] border border-[#EEE9E0] p-3.5 flex flex-col sm:flex-row sm:items-center gap-3">
        <code className="flex-1 text-[13px] text-[#22384F] break-all" data-testid="link-builder-output">
          {url || "Choose a source and type a campaign name to build your link."}
        </code>
        {url && (
          <div className="flex gap-2">
            <CopyButton text={url} testid="link-builder-copy-button" />
            <button type="button" onClick={save} data-testid="link-builder-save-button"
              className="shrink-0 px-3 py-2 rounded-full border border-[#334E68] text-[#334E68] hover:bg-[#334E68]/5 text-[13px] font-semibold transition-colors">
              Save
            </button>
          </div>
        )}
      </div>
      {error && <p className="text-[13px] text-[#B42318] mt-2" role="alert">{error}</p>}
      <h3 className="text-[13px] font-semibold text-[#1A2A3A] mt-6 mb-2">Saved links</h3>
      <ul className="divide-y divide-[#EEE9E0]" data-testid="link-builder-saved-list">
        {saved.length === 0 && <li className="py-3 text-[13px] text-[#5B6B7B]">No saved links yet.</li>}
        {saved.map((l) => (
          <li key={l.id} className="py-3 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2" data-testid={`saved-link-${l.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-[#1A2A3A]">{l.source} · {l.campaign}</div>
              <div className="text-[12px] text-[#5B6B7B] break-all">{l.url}</div>
            </div>
            <div className="flex gap-2">
              <CopyButton text={l.url} testid={`saved-link-copy-${l.id}`} />
              <button type="button" onClick={() => remove(l.id)} aria-label="Delete saved link" data-testid={`saved-link-delete-${l.id}`}
                className="p-2 rounded-full text-[#5B6B7B] hover:text-[#B42318] hover:bg-[#B42318]/5 transition-colors">
                <Trash2 size={15} />
              </button>
            </div>
            <CampaignNotes link={l} token={token}
              onSaved={(doc) => setSaved((list) => list.map((x) => (x.id === doc.id ? doc : x)))} />
          </li>
        ))}
      </ul>
    </section>
  );
}
